// src/pages/Dashboard.jsx
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Trash2, Search, ChevronDown } from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import toast from "react-hot-toast";
import { matchSorter } from "match-sorter";
import placeholderImage from "../assets/placeholder.jpg";
import { useProductStore } from "../store/useProductStore";

/** ---------- Helpers ---------- */
const getTagStyle = (tag) => {
  switch (tag) {
    case "Photoshoot":
      return "bg-blue-600";
    case "Ad Image":
      return "bg-purple-600";
    case "Ad Video":
      return "bg-orange-600";
    case "Product Page":
      return "bg-green-600";
    case "Competition":
      return "bg-yellow-600 text-black";
    default:
      return "bg-pink-600";
  }
};

const tagFromProject = (p) => {
  if (p.asset_type) return p.asset_type;
  if (p.type) return p.type;
  if (p.category) return p.category;
  return ""; // no default label
};

const hostFromUrl = (url) => {
  try {
    const u = new URL(url);
    return u.hostname.replace("www.", "");
  } catch {
    return "";
  }
};

/** Same-size dropdown as ProjectDetails (no label here) */
function SortDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const opts = [
    { value: "Newest first", label: "Newest first" },
    { value: "Oldest first", label: "Oldest first" },
  ];

  const current = opts.find((o) => o.value === value)?.label || "Newest first";

  return (
    <div className="relative" ref={rootRef} style={{ width: 224 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={[
          "group relative flex items-center justify-between gap-2",
          "rounded-2xl px-3 h-10",
          "bg-[#17172a] text-white text-sm",
          "border border-[#343454]",
          "shadow-[inset_0_0_0_1px_rgba(255,255,255,0.02)]",
          "transition-all focus:outline-none",
          open ? "ring-2 ring-pink-500/40 border-pink-500/50" : "hover:border-pink-500/30",
        ].join(" ")}
        style={{ width: 224 }}
      >
        <span className="truncate">{current}</span>
        <ChevronDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        <span className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/5" />
      </button>

      {open && (
        <ul
          role="listbox"
          tabIndex={-1}
          className={[
            "absolute left-0 top-full mt-2",
            "z-50 rounded-2xl",
            "bg-[#151528]/95 backdrop-blur-sm",
            "border border-[#343454]",
            "shadow-2xl shadow-black/40",
            "max-h-72 overflow-auto",
            "animate-[fadeIn_120ms_ease-out]",
          ].join(" ")}
          style={{ width: 224 }}
        >
          {opts.map((o) => {
            const isSel = o.value === value;
            return (
              <li
                key={o.value}
                role="option"
                aria-selected={isSel}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={[
                  "cursor-pointer px-3 py-2 text-sm select-none",
                  "flex items-center justify-between",
                  isSel ? "bg-pink-600/15 text-pink-300" : "hover:bg-[#232340]",
                ].join(" ")}
              >
                <span className="truncate">{o.label}</span>
                {isSel ? <ChevronDown size={18} className="opacity-0" /> : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Count helpers from /api/projects/:id payload */
function countFromProjectDetails(payload) {
  // Prefer normalized assets array if available (Cloudinary-first pattern)
  const list = Array.isArray(payload?.assets) ? payload.assets : [];
  let images = 0;
  let videos = 0;

  list.forEach((a) => {
    const t = String(a?.type || a?.resource_type || "").toLowerCase();
    if (t.includes("video")) videos += 1;
    else images += 1;
  });

  // Fallbacks from legacy fields if present
  if (images === 0 && videos === 0) {
    images =
      payload?.images_count ??
      payload?.asset_counts?.images ??
      payload?.images_total ??
      0;
    videos =
      payload?.videos_count ??
      payload?.asset_counts?.videos ??
      payload?.videos_total ??
      0;
  }

  return { images, videos };
}

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // One dropdown under the title that controls ordering (Newest/Oldest)
  const [sortChoice, setSortChoice] = useState("Newest first");

  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { setSelectedProduct, setSelectedProjectGeneratedAssets } = useProductStore();

  // Fetch projects list on mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        const list = Array.isArray(res.data) ? res.data : [];

        // Enrich each project with image/video counts by reading /api/projects/:id (server already has assets)
        const withCounts = await Promise.all(
          list.map(async (p) => {
            try {
              const details = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/projects/${p.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              const { images, videos } = countFromProjectDetails(details.data || {});
              return { ...p, images_count: images, videos_count: videos };
            } catch {
              return { ...p, images_count: 0, videos_count: 0 };
            }
          })
        );

        setProjects(withCounts);
      } catch (err) {
        console.error("Failed to fetch projects:", err);
      }
    };
    fetchProjects();
  }, [getToken]);

  // Fetch generated assets context when opening details (unchanged)
  const fetchGeneratedAssets = useCallback(
    async (projectId) => {
      try {
        const token = await getToken();
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const {
          photoshoot_images,
          photoshoot_anyshoot_images,
          product_avatar_images,
          product_avatar_videos,
          marketing_videos,
          removed_background_images,
        } = res.data || {};

        return {
          photoshoot_images,
          photoshoot_anyshoot_images,
          product_avatar_images,
          product_avatar_videos,
          marketing_videos,
          removed_background_images,
        };
      } catch (err) {
        console.error("Failed to fetch generated assets", err);
        toast.error("Failed to load project assets");
        return null;
      }
    },
    [getToken]
  );

  const handleProjectSelect = async (project) => {
    setSelectedProduct(project);
    const generatedAssets = await fetchGeneratedAssets(project.id);
    if (generatedAssets) {
      setSelectedProjectGeneratedAssets(generatedAssets);
    }
    navigate(`/project/${project.id}`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      const token = await getToken();
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setProjects((prev) => prev.filter((p) => p.id !== id));
      toast.success("Project deleted");
      window.dispatchEvent(new Event("projectListUpdated"));
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Delete failed");
    }
  };

  // Search + sort
  const filteredProjects = useMemo(() => {
    // Search
    const searched = matchSorter(projects, searchQuery, {
      keys: [(item) => item.product_name || "Untitled Product"],
      threshold: matchSorter.rankings.CONTAINS,
    });

    // Sort by created_at
    const sorted = searched.slice().sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return sortChoice === "Newest first" ? db - da : da - db;
    });

    return sorted;
  }, [projects, searchQuery, sortChoice]);

  return (
    <div className="w-full px-4 sm:px-8 py-8">
      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl px-8 py-10 shadow-lg">
        {/* Title */}
        <h1
          className="text-3xl font-bold text-white mb-2 transition-all duration-300 hover:text-pink-500 hover:drop-shadow-[0_0_10px_rgba(255,0,128,0.5)]"
          aria-label="Project list heading"
        >
          Project List
        </h1>

        {/* Row under title: Sort dropdown (left, same size/position as ProjectDetails) + Search (right) */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <SortDropdown value={sortChoice} onChange={setSortChoice} />

          <div className="relative w-72">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 bg-[#1F1F2F] text-white rounded-md px-4 pl-10 border border-[#3A3A50] focus:outline-none focus:border-pink-500 transition"
              aria-label="Search projects"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
        </div>

        {/* Project Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {/* Analyze CTA Card */}
          <div
            onClick={() => navigate("/analyze")}
            className="flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A50] text-white rounded-lg h-[320px] cursor-pointer hover:border-pink-500 hover:text-pink-500 transition text-center p-6"
            role="button"
            tabIndex={0}
            aria-label="Analyze a product"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") navigate("/analyze");
            }}
          >
            <Plus className="w-8 h-8 mb-2" aria-hidden="true" />
            <p className="font-semibold text-base">Analyze a Product</p>
            <p className="text-sm text-gray-400 mt-1">Start by analyzing a product URL</p>
          </div>

          {/* Project Tiles */}
          {filteredProjects.map((project) => {
            const tag = tagFromProject(project);
            const thumb = project.thumbnail_url || placeholderImage;
            const title = project.product_name || "Untitled Product";
            const created = project.created_at ? new Date(project.created_at).toLocaleDateString() : "";
            const host = project.product_url ? hostFromUrl(project.product_url) : "";

            const imagesCount = Number(project.images_count || 0);
            const videosCount = Number(project.videos_count || 0);

            return (
              <div
                key={project.id}
                className="relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border border-[#2A2A40] text-white rounded-2xl p-3 flex items-center justify-center transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30 min-h-[180px] h-[320px] overflow-hidden cursor-pointer"
                onClick={() => handleProjectSelect(project)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleProjectSelect(project);
                    e.preventDefault();
                  }
                }}
                title={title}
              >
                {/* Top-left tag pill (only if present) */}
                {tag ? (
                  <div className="absolute top-3 left-3 z-10">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border border-white/10 ${getTagStyle(tag)}`}>
                      {tag}
                    </span>
                  </div>
                ) : null}

                {/* Top-right delete (on hover) */}
                <div className="absolute top-3 right-3 z-20 opacity-0 group-hover:opacity-100 transition">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(project.id);
                    }}
                    className="bg-black/80 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition"
                    title="Delete project"
                    aria-label={`Delete project ${title}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {/* Bigger Media area */}
                <img
                  src={thumb}
                  alt={`${title} thumbnail`}
                  className="object-contain max-h-[80%] w-full mx-auto rounded-lg transition-opacity duration-300 group-hover:opacity-85"
                  onError={(e) => {
                    e.currentTarget.src = placeholderImage;
                  }}
                  draggable={false}
                  style={{ background: "#111124" }}
                />

                {/* Bottom metadata strip with clear truncated title and colored counts */}
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <h3 className="text-sm sm:text-[15px] font-semibold truncate" title={title}>
                      {title}
                    </h3>
                    <span className="text-xs text-white/60 select-none">{created}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-white/60 truncate">{host}</span>

                    <div className="flex items-center gap-2">
                      {/* Images count (pink) */}
                      <div
                        className="h-7 px-2 rounded-full bg-pink-600/20 border border-pink-500/40 flex items-center justify-center text-[11px] text-pink-200 font-medium"
                        title={`${imagesCount} images`}
                      >
                        {imagesCount} img
                      </div>
                      {/* Videos count (blue) */}
                      <div
                        className="h-7 px-2 rounded-full bg-blue-600/20 border border-blue-500/40 flex items-center justify-center text-[11px] text-blue-200 font-medium"
                        title={`${videosCount} videos`}
                      >
                        {videosCount} vid
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
